import type { MessageA2uiSurface } from '../types/chat';

export type A2uiComponent = Record<string, any>;
const aliases: Record<string, Record<string, string>> = {
  Text: { variant: 'usageHint' },
  TextField: { value: 'text' },
  ChoicePicker: { choices: 'options', values: 'selections', value: 'selections' },
  Slider: { min: 'minValue', max: 'maxValue' },
};

/** Bounded compatibility normalization: objects/null must never become option strings. */
export function normalizeA2uiComponent(raw: A2uiComponent): A2uiComponent {
  const c = { ...raw };
  for (const [alias, key] of Object.entries(aliases[c.component] ?? {})) {
    if (!(alias in c)) continue;
    if (key in c && JSON.stringify(c[key]) !== JSON.stringify(c[alias])) {
      console.warn(`[A2UI] conflicting ${alias}/${key}; using ${key}`);
    }
    if (!(key in c)) c[key] = c[alias];
    delete c[alias];
  }
  if (c.component === 'Card' && c.child) {
    c.children = c.children ?? [c.child];
    delete c.child;
  }
  if (Array.isArray(c.options)) {
    c.options = c.options
      .map((o: any) => ({
        ...o,
        value:
          typeof o?.value === 'number' && Number.isFinite(o.value) ? String(o.value) : o?.value,
      }))
      .filter((o: any) => {
        const valid = typeof o.value === 'string' && o.value.trim().length > 0;
        if (!valid) console.warn('[A2UI] ignored empty or invalid option');
        return valid;
      });
    if (!c.options.length) throw new Error('No valid choices');
  }
  for (const [prop, field] of [
    ['columns', 'field'],
    ['series', 'key'],
  ]) {
    if (Array.isArray(c[prop]))
      c[prop] = c[prop].map((entry: any) => ({
        ...entry,
        [field]:
          typeof entry?.[field] === 'number' && Number.isFinite(entry[field])
            ? String(entry[field])
            : entry?.[field],
      }));
  }
  for (const key of [
    'minValue',
    'maxValue',
    'step',
    'pageSize',
    'minAllowedSelections',
    'maxAllowedSelections',
  ]) {
    if (typeof c[key] === 'string' && c[key].trim() && Number.isFinite(Number(c[key])))
      c[key] = Number(c[key]);
  }
  return c;
}

export function collectA2uiComponents(surface: MessageA2uiSurface): Map<string, A2uiComponent> {
  const result = new Map<string, A2uiComponent>();
  for (const message of surface.messages) {
    const components = (message.updateComponents as any)?.components;
    if (Array.isArray(components))
      for (const c of components) {
        if (c && typeof c.id === 'string') result.set(c.id, normalizeA2uiComponent(c));
      }
  }
  return result;
}

export const isInteractiveA2uiSurface = (surface: MessageA2uiSurface): boolean =>
  surface.messages.some((message) => {
    const components = (message.updateComponents as any)?.components;
    return (
      Array.isArray(components) &&
      components.some((c: any) => c?.component === 'Button' && c.action?.event)
    );
  });

export const a2uiBindingPath = (c: A2uiComponent): string | undefined =>
  [c.text, c.selections, c.values, c.value].find((v) => v && typeof v.path === 'string')?.path;

/** Validate the fields bound by THIS action; false and zero are meaningful answers. */
export function isA2uiActionValid(
  components: Iterable<A2uiComponent>,
  context: Record<string, unknown>,
  bindings: Record<string, any> = {}
): boolean {
  const fields = [...components];
  for (const [key, binding] of Object.entries(bindings)) {
    const c = fields.find(
      (field) => field.id === key || (binding?.path && a2uiBindingPath(field) === binding.path)
    );
    if (!c || !a2uiBindingPath(c)) continue;
    const value = context[key];
    const required = c.required ?? (key !== 'instructions' && c.id !== 'instructions');
    const empty =
      value == null ||
      (typeof value === 'string' && !value.trim()) ||
      (Array.isArray(value) && value.length === 0);
    if (empty) {
      if (required) return false;
      else continue;
    }
    if (['ChoicePicker', 'MultiSelect'].includes(c.component)) {
      if (
        !Array.isArray(value) ||
        value.length < (c.minAllowedSelections ?? (required ? 1 : 0)) ||
        value.length > (c.maxAllowedSelections ?? Infinity)
      )
        return false;
      if (value.some((v) => !c.options?.some((o: any) => o.value === v))) return false;
    }
    if (c.component === 'Select' && !c.options?.some((o: any) => o.value === value)) return false;
    if (c.component === 'CheckBox' && typeof value !== 'boolean') return false;
    if (
      c.component === 'Slider' &&
      (typeof value !== 'number' ||
        !Number.isFinite(value) ||
        value < (c.minValue ?? 0) ||
        value > (c.maxValue ?? 100))
    )
      return false;
    if (c.component === 'TextField' && c.validationRegexp) {
      try {
        if (!new RegExp(c.validationRegexp).test(String(value))) return false;
      } catch {
        return false;
      }
    }
  }
  return true;
}


export type ChoicePresentation = 'auto' | 'radio' | 'checkbox' | 'dropdown' | 'cards';

/** Presentation never changes cardinality or the array-valued submission contract. */
export function resolveChoicePresentation(
  presentation: ChoicePresentation = 'auto',
  maxAllowedSelections?: number,
  optionCount = 0
): Exclude<ChoicePresentation, 'auto'> {
  const single = maxAllowedSelections === 1;
  if (presentation === 'auto') return single ? (optionCount <= 8 ? 'radio' : 'dropdown') : 'checkbox';
  if ((!single && ['radio', 'dropdown'].includes(presentation)) || (single && presentation === 'checkbox')) {
    throw new Error('ChoicePicker presentation conflicts with maxAllowedSelections');
  }
  return presentation;
}
